import { Button, Empty, EmptyContent, EmptyDescription, EmptyHeader } from '@monksflow/monks-ui';
import { PlusIcon } from 'lucide-react';
import { PERMISSIONS } from '@inference.engine/permissions/web';
import { ScopedPermission } from '@/shared/ui';
import { EmptyStateTitle } from '@/shared/ui';
import type { ProjectsViewState as ViewState } from './ProjectsViewState.types';
import { useTranslation } from 'react-i18next';

interface ProjectsViewStateProps {
  state: ViewState;
}

/**
 * Renders the appropriate UI for each Projects list view state.
 *
 * Receives a discriminated union and renders the matching state via the
 * monks-ui `Empty` primitive. The empty-state "New project" CTA self-gates via
 * `ScopedPermission` (workspace scope), so this reads the permission store.
 * Iconless by design (no `EmptyMedia`): the Projects empty state in Figma
 * (node 5838:171998) is centered title + description + action, no media.
 */
export function ProjectsViewState({ state }: ProjectsViewStateProps) {
  const { t } = useTranslation('projects');

  switch (state.type) {
    case 'error':
      return (
        <Empty role="alert" data-testid="projects-error-state" className="flex-1">
          <EmptyHeader>
            <EmptyStateTitle>
              {t('projectsList.projectsViewState.dataFailedToLoad')}
            </EmptyStateTitle>
            <EmptyDescription>
              {t('projectsList.projectsViewState.somethingWentWrongLoadingYour')}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      );

    case 'no-results':
      return (
        <Empty role="status" data-testid="projects-no-results-state" className="flex-1">
          <EmptyHeader>
            <EmptyStateTitle>
              {t('projectsList.projectsViewState.noMatchingProjects')}
            </EmptyStateTitle>
            <EmptyDescription>
              {t('projectsList.projectsViewState.noProjectsMatchYourCurrent')}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button variant="outline" onClick={state.onClear}>
              {t('projectsList.projectsViewState.clearFilters')}
            </Button>
          </EmptyContent>
        </Empty>
      );

    case 'empty':
      return (
        <Empty role="status" data-testid="projects-empty-state" className="flex-1">
          <EmptyHeader>
            <EmptyStateTitle>{t('projectsList.projectsViewState.projects')}</EmptyStateTitle>
            <EmptyDescription>
              {t('projectsList.projectsViewState.manageAndTrackProjectsBriefs')}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <ScopedPermission scope="workspace" permissions={[PERMISSIONS.ProjectCreate]}>
              <Button onClick={state.onAction}>
                <PlusIcon className="size-4" />
                {t('projectsList.projectsViewState.newProject')}
              </Button>
            </ScopedPermission>
          </EmptyContent>
        </Empty>
      );

    case 'data':
      return null; // Data state is rendered by the parent (toolbar + table)
  }
}
